import { Box, Card, CardContent, Divider, Typography } from "@mui/material";
import localize from "../../common/localize";
import {
  getBarChartJsOptions,
  getDonutChartJsOptions,
  getLineChartJsOptions,
} from "./chartOptionsUtils";
import BarChart from "./charts/BarChart";
import DonutChart from "./charts/DonutChart";
import OperationalStatusStatistics from "./OperationalStatusStatistic";
import ChartJsLineChart from "./charts/ChartJsLineChart";
import { getDatesArray, getValueInCurrency, OperationalStatusEnum } from "../../common/utils";
import { useCallback, useEffect, useMemo, useRef, useState, memo } from "react";
import DonutChartComponent from "./DonutChartComponent";
import BarChartComponent from "./BarChartComponent";
import ModalBackground from "../common/ModalBackground";
import { getCurrencyFromSetup } from "../../common/EntitiesManagement";

const darkgrayColorValue = "#383636";
const getIncomeColor = (value) => {
  if (value > 0) return "green";
  if (value < 0) return "red";
  return darkgrayColorValue
};

const currency = (value) =>
  getValueInCurrency(getCurrencyFromSetup(), value ?? 0, true);

const EventInfoCard = ({ item }) => {
  return (
    <Card sx={{ mb: 2, borderRadius: 2 }} variant="outlined">
      <CardContent sx={{ pb: "12px !important" }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, mb: 1, cursor: "pointer", color: "primary.main" }}
          component="a"
          href={`/events/detail/${item.Id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {item.Code}
        </Typography>

        <Box display="flex" justifyContent="space-between">
          <Typography variant="caption">{localize.service_value}</Typography>
          <Typography variant="caption" sx={{ color: "green", fontWeight: 600 }}>
            + {currency(item.executionFinalValue)}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="caption">{localize.spare_parts_value}</Typography>
          <Typography variant="caption" sx={{ color: "green", fontWeight: 600 }}>
            + {currency(item.sparePartsFinalValue)}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="caption">{localize.additional_costs}</Typography>
          <Typography variant="caption" sx={{ color: "green", fontWeight: 600 }}>
            + {currency(item.additionalCostsValue)}
          </Typography>
        </Box>

         <Divider sx={{ my: 1 }} />

        <Box display="flex" justifyContent="space-between">
          <Typography variant="caption" sx={{ fontWeight: 600 }}>{localize.total_value}</Typography>
          <Typography variant="caption" sx={{ color: darkgrayColorValue, fontWeight: 600 }}>
            {currency(item.totalValue)}
          </Typography>
        </Box>       

        <Box className="mt-4" display="flex" justifyContent="space-between">
          <Typography variant="caption">{localize.payment_to_technician}</Typography>
          <Typography variant="caption" sx={{ color: "red", fontWeight: 600 }}>
            - {currency(item.paymentToTechnician)}
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box display="flex" justifyContent="space-between">
          <Typography variant="caption" sx={{ fontWeight: 600 }}>{localize.real_income}</Typography>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: getIncomeColor(item.totalValue - (item.paymentToTechnician ?? 0)) }}
          >
            {currency(item.totalValue - (item.paymentToTechnician ?? 0))}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const EventsPaymentModalContent = ({ eventsInfo }) => {
  const totalRealIncome = eventsInfo.reduce((acc, item) => {
    const total =
      (item.executionFinalValue ?? 0) +
      (item.sparePartsFinalValue ?? 0) +
      (item.additionalCostsValue ?? 0);
    return acc + total - (item.paymentToTechnician ?? 0);
  }, 0);

  return (
    <Box>
      <Card sx={{ mb: 2, borderRadius: 2, background: "#f5f6f8" }} variant="outlined">
        <CardContent sx={{ textAlign: "center", py: 2, "&:last-child": { pb: 2 } }}>
          <Typography variant="caption" color="text.secondary">
            {localize.real_income}
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: getIncomeColor(totalRealIncome) }}
          >
            {currency(totalRealIncome)}
          </Typography>
        </CardContent>
      </Card>
      {eventsInfo.map((item, index) => (
        <EventInfoCard key={item.id ?? index} item={item} />
      ))}
    </Box>
  );
};

const getOperatinoalStatusData = (data, id) => {
  if (data && id >= -1) {
    if (id === -1) {
      let count = 0;
      data.map((x) => (count += x.dataCount));

      return count;
    }

    const index = data.findIndex((x) => x.operationalStatusId === id);
    return index >= 0 ? data[index].dataCount : 0;
  }

  return 0;
};

const EventStatusChart = ({ data, height, width }) => {
  const { chartData, chartOptions } = getDonutChartJsOptions({
    data,
    type: "status",
  });

  return (
    <DonutChart
      chartData={chartData}
      chartOptions={chartOptions}
      height={height}
      width={width}
    />
  );
};

const EventPersonalChart = ({ data, height, width }) => {
  const { chartData, chartOptions } = getBarChartJsOptions({
    data,
    type: "personal",
    horizontalBar: true,
  });

  return (
    <BarChart
      chartData={chartData}
      chartOptions={chartOptions}
      height={height}
      width={width}
    />
  );
};

const EventTypeChart = ({ data, height, width }) => {
  const { chartData, chartOptions } = getBarChartJsOptions({
    data,
    type: "type",
    horizontalBar: true,
  });

  return (
    <BarChart
      chartData={chartData}
      chartOptions={chartOptions}
      height={height}
      width={width}
    />
  );
};

const EventByProceedingChart = ({ data, height, width }) => {
  const { chartData, chartOptions } = getBarChartJsOptions({
    data,
    type: "proceeding",
    horizontalBar: true,
  });

  return (
    <BarChart
      chartData={chartData}
      chartOptions={chartOptions}
      height={height}
      width={width}
    />
  );
};

const EventsByStateChart = ({ data, height, width }) => {
  const { chartData, chartOptions } = getDonutChartJsOptions({
    data,
    type: "state",
  });

  return (
    <DonutChart
      chartData={chartData}
      chartOptions={chartOptions}
      height={height}
      width={width}
    />
  );
};

const EventsByCityChart = ({ data, height, width }) => {
  const { chartData, chartOptions } = getDonutChartJsOptions({
    data,
    type: "city",
  });

  return (
    <DonutChart
      chartData={chartData}
      chartOptions={chartOptions}
      height={height}
      width={width}
    />
  );
};

const EventsPaymentChart = memo(function EventsPaymentChart({
  data,
  initialDate,
  finalDate,
  isMobile,
  onSelectDate,
}) {
  const onSelectDateRef = useRef(onSelectDate);
  onSelectDateRef.current = onSelectDate;

  const datesArray = useMemo(
    () => getDatesArray(initialDate, finalDate),
    [initialDate, finalDate]
  );

  const { chartData, chartOptions } = useMemo(
    () => getLineChartJsOptions({ data, initialDate, finalDate, isMobile }),
    [data, initialDate, finalDate, isMobile]
  );

  const handlePointClick = useCallback(
    (index) => {
      if (datesArray[index]) {
        onSelectDateRef.current?.(datesArray[index]);
      }
    },
    [datesArray]
  );

  return (
    <div style={{ position: 'relative', height: 350 }}>
      <ChartJsLineChart
        data={chartData}
        options={chartOptions}
        height={350}
        onPointClick={handlePointClick}
      />
    </div>
  );
});

export default function GeneralStatistics({
  eventsByOperationalStatus,
  eventsByStatus,
  eventsDistributionOnPersonal,
  eventsPayment,
  eventsByState,
  eventsByCity,
  eventsByType,
  eventsByProceeding,
  initialDate,
  finalDate,
}) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 767);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedEventsInfo, setSelectedEventsInfo] = useState([]);
  const [selectedDateLabel, setSelectedDateLabel] = useState("");

  const handleSelectDate = useCallback(
    (dateLabel) => {
      const item = eventsPayment?.find((x) => x.paymentDay === dateLabel);
      setSelectedEventsInfo(item?.eventsInfo ?? []);
      setSelectedDateLabel(dateLabel);
      setShowPaymentModal(true);
    },
    [eventsPayment]
  );

  const handleResize = () => {
    setIsMobile(window.innerWidth < 767);
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <section className="flex-col justify-center">
      <div className="flex-col w-full mt-5">
        <div
          className="flex-col w-full mb-4"
          style={{
            gap: "10px",
            justifyContent: "center",
            padding: "20px",
            boxShadow:
              "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
            borderRadius: "10px",
          }}
        >
          <div className="mb-2">
            <Typography
              sx={{
                fontSize: "0.8em",
                fontWeight: 400,
                textAlign: "center",
              }}
            >
              <b>{localize.operational_status_analytics}</b>
            </Typography>
          </div>

          <div className="flex gap-2 w-full items-center mb-2 justify-evenly flex-wrap">
            <OperationalStatusStatistics
              operationalStatusId={OperationalStatusEnum.ToExecute}
              operationalStatusValue={getOperatinoalStatusData(
                eventsByOperationalStatus,
                OperationalStatusEnum.ToExecute
              )}
              initialDateForFilter={initialDate}
              finalDateForFilter={finalDate}
            />
            <OperationalStatusStatistics
              operationalStatusId={OperationalStatusEnum.InExecution}
              operationalStatusValue={getOperatinoalStatusData(
                eventsByOperationalStatus,
                OperationalStatusEnum.InExecution
              )}
              initialDateForFilter={initialDate}
              finalDateForFilter={finalDate}
            />
            <OperationalStatusStatistics
              operationalStatusId={OperationalStatusEnum.PendingReview}
              operationalStatusValue={getOperatinoalStatusData(
                eventsByOperationalStatus,
                OperationalStatusEnum.PendingReview
              )}
              initialDateForFilter={initialDate}
              finalDateForFilter={finalDate}
            />
            <OperationalStatusStatistics
              operationalStatusId={OperationalStatusEnum.Finished}
              operationalStatusValue={getOperatinoalStatusData(
                eventsByOperationalStatus,
                OperationalStatusEnum.Finished
              )}
              initialDateForFilter={initialDate}
              finalDateForFilter={finalDate}
            />
            <OperationalStatusStatistics
              operationalStatusId={OperationalStatusEnum.NotFinished}
              operationalStatusValue={getOperatinoalStatusData(
                eventsByOperationalStatus,
                OperationalStatusEnum.NotFinished
              )}
              initialDateForFilter={initialDate}
              finalDateForFilter={finalDate}
            />
            <OperationalStatusStatistics
              operationalStatusId={OperationalStatusEnum.Canceled}
              operationalStatusValue={getOperatinoalStatusData(
                eventsByOperationalStatus,
                OperationalStatusEnum.Canceled
              )}
              initialDateForFilter={initialDate}
              finalDateForFilter={finalDate}
            />

            <OperationalStatusStatistics
              operationalStatusId={OperationalStatusEnum.TotalServiceOrders}
              operationalStatusValue={getOperatinoalStatusData(
                eventsByOperationalStatus,
                OperationalStatusEnum.TotalServiceOrders
              )}
              initialDateForFilter={initialDate}
              finalDateForFilter={finalDate}
            />
          </div>
        </div>

        <div className="flex gap-2 w-full">
          {initialDate && finalDate && (
            <div
              className="w-full"
              style={{
                padding: "20px",
                boxShadow:
                  "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
                borderRadius: "10px",
              }}
            >
              <div>
                <EventsPaymentChart
                  data={eventsPayment}
                  initialDate={initialDate}
                  finalDate={finalDate}
                  isMobile={isMobile}
                  onSelectDate={handleSelectDate}
                />
              </div>
              {showPaymentModal && (
                <ModalBackground
                  isDrawerMode
                  title={`${localize.events} — ${selectedDateLabel}`}
                  showModal={showPaymentModal}
                  setShowModal={setShowPaymentModal}
                  cancelButtonText={localize.close}
                  hideSubmitButton
                  onClickToCancel={() => setShowPaymentModal(false)}
                  Component={
                    <EventsPaymentModalContent eventsInfo={selectedEventsInfo} />
                  }
                />
              )}
            </div>
          )}
        </div>
      </div>
      <div className="mt-5 w-full flex gap-2 flex-wrap justify-between">
        {eventsByStatus?.length > 0 && (
          <DonutChartComponent
            title={localize.execution_status_analytics}
            chartComponent={
              <EventStatusChart
                width="100%"
                height={"280px"}
                data={eventsByStatus}
              />
            }
          />
        )}

        {eventsByState?.length > 0 && (
          <DonutChartComponent
            title={localize.events_by_state_analytics}
            chartComponent={
              <EventsByStateChart
                width="100%"
                height={"280px"}
                data={eventsByState}
              />
            }
          />
        )}

        {eventsByCity?.length > 0 && (
          <DonutChartComponent
            title={localize.events_by_city_analytics}
            chartComponent={
              <EventsByCityChart
                width="100%"
                height={"280px"}
                data={eventsByCity}
              />
            }
          />
        )}
      </div>

      <div className="mt-5 w-full flex-wrap flex gap-2 flex-wrap justify-between">
        {eventsByType?.length > 0 && (
          <BarChartComponent
            title={localize.events_by_type}
            chartComponent={<EventTypeChart width="100%" data={eventsByType} />}
          />
        )}

        {eventsByProceeding?.length > 0 && (
          <BarChartComponent
            title={localize.events_by_proceeding}
            chartComponent={
              <EventByProceedingChart width="100%" data={eventsByProceeding} />
            }
          />
        )}

        {eventsDistributionOnPersonal?.length > 0 && (
          <BarChartComponent
            title={localize.events_by_distribution_on_personal}
            chartComponent={
              <EventPersonalChart
                width="100%"
                data={eventsDistributionOnPersonal}
              />
            }
          />
        )}
      </div>
    </section>
  );
}
